import React from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { queryClient } from 'src/lib/react-query';
import { generalMessages } from 'src/registry/utils/messages';
import { toast } from 'src/hooks/useToast';
import { cn } from 'src/utils';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { useCreateSubscriptionItemQuantityEvent } from 'src/features/subscriptionItemQuantityEvents/api/useCreateSubscriptionItemQuantityEvent';
import { LIST_SUBSCRIPTION_ITEM_QUANTITY_EVENTS_RESOURCE } from 'src/features/subscriptionItemQuantityEvents/api/useListSubscriptionItemQuantityEvents';
import { subItemQuantityEventMessages } from 'src/features/subscriptionItemQuantityEvents/utils/messages';
import { subscriptionItemQuantityEventFormSchema, SubscriptionItemQuantityEventFormSchemaValues } from 'src/features/subscriptionItemQuantityEvents/utils/formSchema';
import { SUBSCRIPTION_ITEM_QUANTITY_EVENT_TYPE } from 'src/features/subscriptionItemQuantityEvents/utils/types';
import SubscriptionItemQuantityEventForm from 'src/features/subscriptionItemQuantityEvents/components/forms/SubscriptionItemQuantityEventForm';

/**
 * Manual entry for quantity events. Billing is operated by hand for now, so these are typed in
 * rather than ingested from an API — the same records the engine would have received.
 */
function CreateSubscriptionItemQuantityEventFormModal<TData>({ className }: DataTableNewEntryProps<TData>) {
    const { formatMessage } = useIntl();
    const { subscriptionItemId = '' } = useParams();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const onError = useOnError();

    const form = useForm<SubscriptionItemQuantityEventFormSchemaValues>({
        resolver: zodResolver(subscriptionItemQuantityEventFormSchema),
        defaultValues: { recorded_at: new Date().toISOString(), event_type: SUBSCRIPTION_ITEM_QUANTITY_EVENT_TYPE.SET, quantity: 0 },
    });

    const { mutate, isPending } = useCreateSubscriptionItemQuantityEvent({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTION_ITEM_QUANTITY_EVENTS_RESOURCE] });
            setIsModalOpen(false);
            form.reset();
            toast({
                title: formatMessage(subItemQuantityEventMessages.createQuantityEventSuccess),
                description: formatMessage(subItemQuantityEventMessages.createQuantityEventSuccessDescription),
            });
        },
    });

    const title = formatMessage(generalMessages.createEntry, {
        entry: formatMessage(subItemQuantityEventMessages.quantityEvent).toLowerCase(),
    });

    const onSubmit = (values: SubscriptionItemQuantityEventFormSchemaValues) => {
        mutate(
            { subscription_item: subscriptionItemId, ...values },
            { onError: (error) => onError(error, form.setError) },
        );
    };

    return (
        <Dialog modal open={isModalOpen} onOpenChange={(open) => {
            if (!open) form.reset();
            setIsModalOpen(open);
        }}>
            <DialogTrigger asChild>
                <Button className={cn('ml-auto', className)}>
                    <FontAwesomeIcon icon={ICON_MAP.ADD} />
                    {title}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.ADD} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{formatMessage(subItemQuantityEventMessages.addQuantityEventSubtitle)}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <SubscriptionItemQuantityEventForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CreateSubscriptionItemQuantityEventFormModal;
